import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { EMPTY, Observable, catchError, finalize, map, of, tap } from 'rxjs';
import { environment } from '@environment';
import {
  GameSummary,
  StreakGameInfo,
  StreakInitialState,
  WheelResponse,
  WheelSpinResult,
} from '../api/game-api.types';
import { mapQuizToGameSummary, QuizDto } from '../api/quiz-mapper';
import { WheelSegment } from '../models/wheel.types';

interface StreakStateResponse {
  streakGame?: StreakGameInfo;
  quiz?: QuizDto[];
  gameConfig?: string;
  freeSpins?: number;
  streak?: number;
  themes: {
    streaksThemeUrl?: string;
    wheelThemeUrl?: string;
    hubThemeUrl?: string;
  };
  labels?: {
    streaksThemeUrl?: string | null;
    wheelThemeUrl?: string | null;
    hubThemeUrl?: string | null;
  };
  triggerCount?: number;
  hideWheel?: boolean;
}

const DEFAULT_STREAK_GOAL = 5;

@Injectable({ providedIn: 'root' })
export class StreakStateService {
  private http = inject(HttpClient);

  readonly games = signal<GameSummary[]>([]);
  readonly wheelSegments = signal<WheelSegment[]>([]);
  readonly streak = signal(0);
  readonly freeSpins = signal(0);
  readonly triggerCount = signal(DEFAULT_STREAK_GOAL);
  readonly streakGame = signal<StreakGameInfo | null>(null);
  readonly gameConfigUrl = signal<string | null>(null);
  readonly hideWheel = signal(false);

  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly topPrize = signal<string | null>(null);

  readonly lastSpinResult = signal<WheelSpinResult | null>(null);

  readonly wheelLoaded = computed(() => this.wheelSegments().length > 0);

  readonly completedCount = computed(() =>
    Math.min(this.streak(), this.triggerCount()),
  );
  readonly allCompleted = computed(
    () => this.freeSpins() > 0 || this.streak() >= this.triggerCount(),
  );

  private lastGameId: string | null = null;

  refresh(): Observable<StreakInitialState> {
    if (!this.lastGameId) return EMPTY;
    return this.load(this.lastGameId);
  }

  load(gameId: string): Observable<StreakInitialState> {
    this.lastGameId = gameId;
    this.loading.set(true);
    this.errorMessage.set(null);

    return this.http
      .get<StreakStateResponse>(environment.backendUrl, { params: { gameId } })
      .pipe(
        map((res) => this.toInitialState(res)),
        tap((state) => this.apply(state)),
        catchError(() => {
          this.errorMessage.set('Failed to load game. Please try again.');
          return EMPTY;
        }),
        finalize(() => {
          this.loading.set(false);
        }),
      );
  }

  loadWheelSegments(): Observable<WheelSegment[]> {
    return this.http
      .get<WheelResponse>(`${environment.backendUrl}/get-wheel`)
      .pipe(
        map((response) => this.sortWheelSegments(response.segments)),
        tap((segments) => {
          if (!segments.length) {
            console.error(
              '[wheel] Wheel segments not configured. Please configure wheel segments for this game.',
            );
          }
          this.wheelSegments.set(segments);
        }),
        catchError((err) => {
          console.error('[wheel] failed to load segments', err);
          return of([]);
        }),
      );
  }

  private sortWheelSegments(segments: WheelSegment[]): WheelSegment[] {
    return [...segments].sort((a, b) => a.index - b.index);
  }

  applySubmitResult(streak: number, freeSpins: number): void {
    this.streak.set(streak);
    this.freeSpins.set(freeSpins);
  }

  markQuizPlayed(quizId: string): void {
    this.games.update((list) =>
      list.map((g) =>
        g.id === quizId
          ? { ...g, status: g.status === 'live' ? 'played' : g.status }
          : g,
      ),
    );
  }

  applySpinResult(result: WheelSpinResult): void {
    this.lastSpinResult.set(result);
  }

  private toInitialState(res: StreakStateResponse): StreakInitialState {
    return {
      streakGame: res.streakGame,
      games: (res.quiz || []).map(mapQuizToGameSummary),
      gameConfig: res.gameConfig,
      freeSpins: res.freeSpins ?? 0,
      streak: res.streak ?? 0,
      themes: res.themes,
      labels: res.labels,
      triggerCount: res.triggerCount,
      hideWheel: res.hideWheel ?? false,
    };
  }

  private apply(state: StreakInitialState): void {
    this.games.set(state.games);
    this.streak.set(state.streak);
    this.freeSpins.set(state.freeSpins);
    this.streakGame.set(state.streakGame ?? null);
    this.triggerCount.set(
      state.triggerCount ??
        state.streakGame?.triggerCount ??
        DEFAULT_STREAK_GOAL,
    );
    this.gameConfigUrl.set(state.gameConfig ?? null);
    this.hideWheel.set(state.hideWheel ?? false);
    this.topPrize.set(this.computeTopPrize(state.games));
  }

  private computeTopPrize(games: GameSummary[]): string | null {
    let top = '';
    let max = -1;
    for (const g of games) {
      const num = Number((g.prizeValue || '').replace(/[^0-9.]/g, ''));
      if (!Number.isNaN(num) && num > max) {
        max = num;
        top = g.prizeValue;
      }
    }
    return top || null;
  }
}
